import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import {
  XAxis, YAxis, Tooltip, ReferenceLine, ReferenceArea,
  ResponsiveContainer, Area, CartesianGrid, Line, ComposedChart
} from "recharts";
import { MARKET, EVENTS, HISTORY_FULL, DTCC_DATA, DELTA_STRIKES, TENOR_ORDER, TENOR_DAYS } from '../data';
import { AS_OF, C as BASE_C, F, MONO, SANS, ChartHoverLayer } from './ratesShared';
import { S, btn, badge, logoIcon, spotNum, HEADER_H, PANEL_W } from './ui';
// FX uses blue as its accent instead of amber (rates) / purple (curve).
// All other palette tokens (bg, bd, tx…) stay identical for cross-page consistency.
const C = { ...BASE_C, ac: '#60a5fa', acD: 'rgba(96,165,250,.12)' };

// ─── MATH ────────────────────────────────────────────────────────────────────
function normCdf(x){const a1=.254829592,a2=-.284496736,a3=1.421413741,a4=-1.453152027,a5=1.061405429,p=.3275911;const s=x<0?-1:1;x=Math.abs(x)/Math.SQRT2;const t=1/(1+p*x);return .5*(1+s*(1-(((((a5*t+a4)*t)+a3)*t+a2)*t+a1)*t*Math.exp(-x*x)))}
function gkPrice(S,K,T,rd,rf,sig,isC){if(T<=0)return isC?Math.max(S-K,0):Math.max(K-S,0);const sq=Math.sqrt(T),d1=(Math.log(S/K)+(rd-rf+.5*sig*sig)*T)/(sig*sq),d2=d1-sig*sq;return isC?S*Math.exp(-rf*T)*normCdf(d1)-K*Math.exp(-rd*T)*normCdf(d2):K*Math.exp(-rd*T)*normCdf(-d2)-S*Math.exp(-rf*T)*normCdf(-d1)}
function gkDelta(S,K,T,rd,rf,sig,isC){if(T<=0)return isC?(S>K?1:0):(S<K?-1:0);const d1=(Math.log(S/K)+(rd-rf+.5*sig*sig)*T)/(sig*Math.sqrt(T));return isC?Math.exp(-rf*T)*normCdf(d1):-Math.exp(-rf*T)*normCdf(-d1)}
function interpVol(pts,K){if(K<=pts[0].K)return pts[0].vol;if(K>=pts[pts.length-1].K)return pts[pts.length-1].vol;for(let i=0;i<pts.length-1;i++){if(K>=pts[i].K&&K<=pts[i+1].K){const w=(K-pts[i].K)/(pts[i+1].K-pts[i].K);return pts[i].vol+w*(pts[i+1].vol-pts[i].vol)}}return pts[2].vol}
function getVol(K,tk){const t=MARKET.tenors[tk];return t?interpVol(t.points,K):.08}
function priceOpt(K,tk,isC){const S=MARKET.spot,t=MARKET.tenors[tk];if(!t)return{premium:0,delta:0,vol:.08,K,T:0,isC,fwd:S};const sig=getVol(K,tk);return{premium:gkPrice(S,K,t.T,t.rd,t.rf,sig,isC),delta:gkDelta(S,K,t.T,t.rd,t.rf,sig,isC),vol:sig,K,T:t.T,isC,fwd:t.fwd}}

// ─── RISK-NEUTRAL DENSITY ─────────────────────────────────────────────────────
function smoothVol(K,tk){const t=MARKET.tenors[tk];if(!t)return .08;const pts=t.points;if(K<=pts[0].K)return pts[0].vol;if(K>=pts[pts.length-1].K)return pts[pts.length-1].vol;for(let i=0;i<pts.length-1;i++){if(K>=pts[i].K&&K<=pts[i+1].K){const u=(K-pts[i].K)/(pts[i+1].K-pts[i].K),u2=u*u,u3=u2*u;const p0=i>0?pts[i-1].vol:pts[i].vol,p1=pts[i].vol,p2=pts[i+1].vol,p3=i<pts.length-2?pts[i+2].vol:pts[i+1].vol;return .5*((2*p1)+(-p0+p2)*u+(2*p0-5*p1+4*p2-p3)*u2+(-p0+3*p1-3*p2+p3)*u3)}}return pts[2].vol}
function gaussSmooth(a,sig){const n=a.length,o=new Array(n),r=Math.ceil(sig*3);for(let i=0;i<n;i++){let s=0,w=0;for(let j=Math.max(0,i-r);j<=Math.min(n-1,i+r);j++){const g=Math.exp(-.5*((j-i)/sig)**2);s+=a[j]*g;w+=g}o[i]=s/w}return o}
function computeRNDensity(tk,n=150){const S=MARKET.spot,t=MARKET.tenors[tk];if(!t)return null;const{T,rd,rf}=t,lo=S*.82,hi=S*1.18,dK=(hi-lo)/n,bump=dK*3;const Ks=[],raw=[];for(let i=0;i<=n;i++){const K=lo+i*dK;Ks.push(K);const Cup=gkPrice(S,K+bump,T,rd,rf,smoothVol(K+bump,tk),true),Cmid=gkPrice(S,K,T,rd,rf,smoothVol(K,tk),true),Cdn=gkPrice(S,K-bump,T,rd,rf,smoothVol(K-bump,tk),true);raw.push(Math.max((Cup-2*Cmid+Cdn)/(bump*bump)*Math.exp(rd*T),0))}const pdf=gaussSmooth(raw,Math.max(3,Math.round(n/40)));let tot=0;for(let i=0;i<Ks.length-1;i++)tot+=.5*(pdf[i]+pdf[i+1])*(Ks[i+1]-Ks[i]);if(tot>0)for(let i=0;i<pdf.length;i++)pdf[i]/=tot;return{Ks,pdf}}
function probWeighted(pd,density){if(!density||!pd)return{er:0,pp:0};const{Ks,pdf}=density;let er=0,pp=0;for(let i=0;i<Ks.length-1;i++){const K=(Ks[i]+Ks[i+1])/2,w=.5*(pdf[i]+pdf[i+1])*(Ks[i+1]-Ks[i]);let pnl=0;for(let j=0;j<pd.length-1;j++){if(pd[j].spot<=K&&pd[j+1].spot>=K){pnl=pd[j].pnl+(K-pd[j].spot)/(pd[j+1].spot-pd[j].spot)*(pd[j+1].pnl-pd[j].pnl);break}}er+=pnl*w;if(pnl>0)pp+=w}return{er,pp}}

// ─── CUSTOM TENOR BUILDER ─────────────────────────────────────────────────────
function addDays(ds,n){const d=new Date(ds);d.setDate(d.getDate()+n);return d.toISOString().slice(0,10)}

function buildCustomFxTenor(T_req){
  const ordered=TENOR_ORDER.filter(t=>MARKET.tenors[t]).sort((a,b)=>MARKET.tenors[a].T-MARKET.tenors[b].T);
  if(ordered.length<2)return null;
  const Ts=ordered.map(k=>MARKET.tenors[k].T);
  const T=Math.max(Ts[0],Math.min(Ts[Ts.length-1],T_req));
  let loKey=ordered[0],hiKey=ordered[ordered.length-1];
  for(let i=0;i<ordered.length-1;i++){if(MARKET.tenors[ordered[i]].T<=T&&MARKET.tenors[ordered[i+1]].T>=T){loKey=ordered[i];hiKey=ordered[i+1];break;}}
  const lo=MARKET.tenors[loKey],hi=MARKET.tenors[hiKey];
  const w=lo.T===hi.T?0:(T-lo.T)/(hi.T-lo.T);
  const lerp=(a,b)=>a+w*(b-a);
  const fwd=lerp(lo.fwd,hi.fwd);
  const points=hi.points.map(pt=>{
    const moneyness=pt.K/hi.fwd;
    return{K:moneyness*fwd, vol:lerp(interpVol(lo.points,moneyness*lo.fwd),interpVol(hi.points,pt.K))};
  });
  return{T,rd:lerp(lo.rd,hi.rd),rf:lerp(lo.rf,hi.rf),fwd,points};
}

// ─── STRATEGY EVALUATION ─────────────────────────────────────────────────────
function evalStrat(st, tgt) {
  const S = MARKET.spot;
  const spots = [];
  for (let s = S * .85; s <= S * 1.15; s += S * .002) spots.push(s);
  let netP = 0;
  st.legs.forEach(l => { const r = priceOpt(l.K, l.tenor, l.isCall); netP += r.premium * l.dir * l.ratio; });
  const pf = spots.map(s => {
    let p = 0;
    st.legs.forEach(l => { p += (l.isCall ? Math.max(s - l.K, 0) : Math.max(l.K - s, 0)) * l.dir * l.ratio; });
    return p - netP;
  });
  let tPnl = 0;
  st.legs.forEach(l => { tPnl += (l.isCall ? Math.max(tgt - l.K, 0) : Math.max(l.K - tgt, 0)) * l.dir * l.ratio; });
  tPnl -= netP;
  const mxL = -Math.min(...pf), mxG = Math.max(...pf);
  const bfb = mxL > 1e-6 ? tPnl / mxL : 0;
  const be = [];
  for (let i = 0; i < pf.length - 1; i++) {
    if (pf[i] * pf[i+1] < 0) {
      const s1 = spots[i], s2 = spots[i+1];
      be.push(s1 - pf[i] * (s2 - s1) / (pf[i+1] - pf[i]));
    }
  }
  let nD = 0;
  st.legs.forEach(l => { nD += priceOpt(l.K, l.tenor, l.isCall).delta * l.dir * l.ratio; });
  return { ...st, netPrem: netP, targetPnl: tPnl, maxLoss: mxL, maxGain: mxG, bfb, breakevens: be, netDelta: nD, payoffData: spots.map((s, i) => ({ spot: s, pnl: pf[i] })), premPct: netP / S * 100 };
}

function evalStratRange(st, lo, hi) {
  const S = MARKET.spot;
  const spots = [];
  for (let s = S * .85; s <= S * 1.15; s += S * .002) spots.push(s);
  let netP = 0;
  st.legs.forEach(l => { const r = priceOpt(l.K, l.tenor, l.isCall); netP += r.premium * l.dir * l.ratio; });
  const pf = spots.map(s => {
    let p = 0;
    st.legs.forEach(l => { p += (l.isCall ? Math.max(s - l.K, 0) : Math.max(l.K - s, 0)) * l.dir * l.ratio; });
    return p - netP;
  });
  const rPnl = [];
  for (let i = 0; i < spots.length; i++) { if (spots[i] >= lo && spots[i] <= hi) rPnl.push(pf[i]); }
  const minRP = rPnl.length ? Math.min(...rPnl) : 0, avgRP = rPnl.length ? rPnl.reduce((a, b) => a + b, 0) / rPnl.length : 0;
  const mxL = -Math.min(...pf), mxG = Math.max(...pf);
  const bfb = mxL > 1e-6 ? avgRP / mxL : 0;
  const be = [];
  for (let i = 0; i < pf.length - 1; i++) {
    if (pf[i] * pf[i+1] < 0) {
      const s1 = spots[i], s2 = spots[i+1];
      be.push(s1 - pf[i] * (s2 - s1) / (pf[i+1] - pf[i]));
    }
  }
  let nD = 0;
  st.legs.forEach(l => { nD += priceOpt(l.K, l.tenor, l.isCall).delta * l.dir * l.ratio; });
  return { ...st, netPrem: netP, minRangePnl: minRP, avgRangePnl: avgRP, maxLoss: mxL, maxGain: mxG, bfb, breakevens: be, netDelta: nD, payoffData: spots.map((s, i) => ({ spot: s, pnl: pf[i] })), premPct: netP / S * 100 };
}

// ─── ARCHETYPE STRATEGY GENERATOR ────────────────────────────────────────────
// Generates one representative per structure type (not all strike permutations).
function genStratArchetypes(tgt, tenors) {
  const S = MARKET.spot;
  const bull = tgt > S;
  const out = [];
  const add = (archetype, legs) => out.push({ archetype, legs });

  for (const tn of tenors) {
    const dist = Math.abs(tgt - S);
    const K_atm = S;
    const K_tgt = tgt;
    const K_mid = (S + tgt) / 2;
    const K_near = bull ? S + dist * 0.3 : S - dist * 0.3; // 30% of the way to target
    const K_opp = bull ? S - dist * 0.5 : S + dist * 0.5; // opposite side for risk reversal

    if (bull) {
      add('Call',              [{ K: K_atm,  tenor: tn, isCall: true,  dir:  1, ratio: 1 }]);
      add('Call (OTM)',        [{ K: K_near, tenor: tn, isCall: true,  dir:  1, ratio: 1 }]);
      add('Call Debit Spread', [{ K: K_atm,  tenor: tn, isCall: true,  dir:  1, ratio: 1 },
                                { K: K_tgt,  tenor: tn, isCall: true,  dir: -1, ratio: 1 }]);
      add('Butterfly',         [{ K: K_atm,  tenor: tn, isCall: true,  dir:  1, ratio: 1 },
                                { K: K_mid,  tenor: tn, isCall: true,  dir: -1, ratio: 2 },
                                { K: K_tgt,  tenor: tn, isCall: true,  dir:  1, ratio: 1 }]);
      add('Risk Reversal',     [{ K: K_opp,  tenor: tn, isCall: false, dir: -1, ratio: 1 },
                                { K: K_tgt,  tenor: tn, isCall: true,  dir:  1, ratio: 1 }]);
      add('Call Ratio Spread', [{ K: K_atm,  tenor: tn, isCall: true,  dir:  1, ratio: 1 },
                                { K: K_tgt,  tenor: tn, isCall: true,  dir: -1, ratio: 2 }]);
    } else {
      add('Put',               [{ K: K_atm,  tenor: tn, isCall: false, dir:  1, ratio: 1 }]);
      add('Put (OTM)',         [{ K: K_near, tenor: tn, isCall: false, dir:  1, ratio: 1 }]);
      add('Put Debit Spread',  [{ K: K_atm,  tenor: tn, isCall: false, dir:  1, ratio: 1 },
                                { K: K_tgt,  tenor: tn, isCall: false, dir: -1, ratio: 1 }]);
      add('Butterfly',         [{ K: K_tgt,  tenor: tn, isCall: false, dir:  1, ratio: 1 },
                                { K: K_mid,  tenor: tn, isCall: false, dir: -1, ratio: 2 },
                                { K: K_atm,  tenor: tn, isCall: false, dir:  1, ratio: 1 }]);
      add('Risk Reversal',     [{ K: K_tgt,  tenor: tn, isCall: false, dir:  1, ratio: 1 },
                                { K: K_opp,  tenor: tn, isCall: true,  dir: -1, ratio: 1 }]);
      add('Put Ratio Spread',  [{ K: K_atm,  tenor: tn, isCall: false, dir:  1, ratio: 1 },
                                { K: K_tgt,  tenor: tn, isCall: false, dir: -1, ratio: 2 }]);
    }
    add('Straddle', [{ K: K_atm, tenor: tn, isCall: true,  dir: 1, ratio: 1 },
                     { K: K_atm, tenor: tn, isCall: false, dir: 1, ratio: 1 }]);
  }
  return out;
}

function genRangeArchetypes(lo, hi, tenors) {
  const S = MARKET.spot;
  const mid = (lo + hi) / 2;
  const wing = (hi - lo) * 0.7;
  const out = [];
  const add = (archetype, legs) => out.push({ archetype, legs });

  for (const tn of tenors) {
    // Iron Condor: sell the inner strikes, buy the outer
    add('Iron Condor', [
      { K: Math.max(lo - wing, S * 0.85), tenor: tn, isCall: false, dir:  1, ratio: 1 },
      { K: lo,                             tenor: tn, isCall: false, dir: -1, ratio: 1 },
      { K: hi,                             tenor: tn, isCall: true,  dir: -1, ratio: 1 },
      { K: Math.min(hi + wing, S * 1.15), tenor: tn, isCall: true,  dir:  1, ratio: 1 },
    ]);
    // Butterfly at mid point
    add('Butterfly', [
      { K: lo,  tenor: tn, isCall: true, dir:  1, ratio: 1 },
      { K: mid, tenor: tn, isCall: true, dir: -1, ratio: 2 },
      { K: hi,  tenor: tn, isCall: true, dir:  1, ratio: 1 },
    ]);
    // Straddle at mid
    add('Straddle', [
      { K: mid, tenor: tn, isCall: true,  dir: 1, ratio: 1 },
      { K: mid, tenor: tn, isCall: false, dir: 1, ratio: 1 },
    ]);
    // Directional spreads if range is entirely above/below spot
    if (S < lo) {
      add('Call Debit Spread', [
        { K: lo, tenor: tn, isCall: true, dir:  1, ratio: 1 },
        { K: hi, tenor: tn, isCall: true, dir: -1, ratio: 1 },
      ]);
    }
    if (S > hi) {
      add('Put Debit Spread', [
        { K: hi, tenor: tn, isCall: false, dir:  1, ratio: 1 },
        { K: lo, tenor: tn, isCall: false, dir: -1, ratio: 1 },
      ]);
    }
  }
  return out;
}

// ─── EXCLUSION FILTER ─────────────────────────────────────────────────────────
// Returns false if a strategy's profit zone contradicts an exclusion zone.
function passesExclusions(strat, exclusions) {
  if (!exclusions || exclusions.length === 0) return true;
  for (const excl of exclusions) {
    const lvl = excl.level;
    if (!lvl || !isFinite(lvl)) continue;
    if (excl.side === 'above') {
      // "spot won't reach above lvl" → exclude strategies that need spot > lvl to profit
      // Check: if the lowest long-call strike is above the exclusion level, exclude
      const longCallKs = strat.legs.filter(l => l.dir > 0 && l.isCall).map(l => l.K);
      if (longCallKs.length > 0 && Math.min(...longCallKs) >= lvl) return false;
    } else {
      // "spot won't reach below lvl" → exclude strategies that need spot < lvl to profit
      const longPutKs = strat.legs.filter(l => l.dir > 0 && !l.isCall).map(l => l.K);
      if (longPutKs.length > 0 && Math.max(...longPutKs) <= lvl) return false;
    }
  }
  return true;
}

// ─── OPTIMIZER ────────────────────────────────────────────────────────────────
function optimize(tgt, tenors, zeroCost, rankMode, dCache, exclusions, riskBudget) {
  let rs = genStratArchetypes(tgt, tenors)
    .map(s => evalStrat(s, tgt))
    .filter(r => r.maxLoss > 1e-6 && r.targetPnl > 0)
    .filter(r => passesExclusions(r, exclusions));
  if (zeroCost) rs = rs.filter(r => Math.abs(r.premPct) < .2);
  rs.forEach(r => {
    const d = dCache[r.legs[0]?.tenor];
    if (d) { const pw = probWeighted(r.payoffData, d); r.er = r.maxLoss > 1e-6 ? pw.er / r.maxLoss : 0; r.pp = pw.pp; }
    else { r.er = 0; r.pp = 0; }
    // Risk-budget implied size
    r.size = riskBudget > 0 && r.maxLoss > 0 ? (riskBudget / 1e4) / r.maxLoss : 1;
  });
  const mxB = Math.max(...rs.map(r => Math.abs(r.bfb)), 1);
  const mxE = Math.max(...rs.map(r => r.er), .001);
  rs.forEach(r => {
    if (rankMode === 'probability') r.score = r.er / mxE;
    else if (rankMode === 'combined') r.score = .5 * r.bfb / mxB + .5 * r.er / mxE;
    else r.score = r.bfb / mxB;
  });
  return rs.sort((a, b) => b.score - a.score);
}

function optimizeRange(lo, hi, tenors, zeroCost, rankMode, dCache, exclusions, riskBudget) {
  let rs = genRangeArchetypes(lo, hi, tenors)
    .map(s => evalStratRange(s, lo, hi))
    .filter(r => r.maxLoss > 1e-6 && r.avgRangePnl > 0)
    .filter(r => passesExclusions(r, exclusions));
  if (zeroCost) rs = rs.filter(r => Math.abs(r.premPct) < .2);
  rs.forEach(r => {
    const d = dCache[r.legs[0]?.tenor];
    if (d) { const pw = probWeighted(r.payoffData, d); r.er = r.maxLoss > 1e-6 ? pw.er / r.maxLoss : 0; r.pp = pw.pp; }
    else { r.er = 0; r.pp = 0; }
    r.size = riskBudget > 0 && r.maxLoss > 0 ? (riskBudget / 1e4) / r.maxLoss : 1;
  });
  const mxB = Math.max(...rs.map(r => Math.abs(r.bfb)), 1);
  const mxE = Math.max(...rs.map(r => r.er), .001);
  rs.forEach(r => {
    if (rankMode === 'probability') r.score = r.er / mxE;
    else if (rankMode === 'combined') r.score = .5 * r.bfb / mxB + .5 * r.er / mxE;
    else r.score = r.bfb / mxB;
  });
  return rs.sort((a, b) => b.score - a.score);
}

// ─── SHARED STYLE TOKENS ─────────────────────────────────────────────────────
// (C imported from ratesShared — same dark palette)
const MA_COLORS = { ma50: '#f0883e', ma100: '#a371f7', ma200: '#f778ba' };
const HIST_PERIODS = { "6M": 126, "1Y": 252, "2Y": 504, "3Y": 750 };

// btn() is imported from ui.js — no local copy needed.

function safeFmt(v, d = 2) { return (typeof v === 'number' && isFinite(v)) ? v.toFixed(d) : ''; }

// ─── MOVING AVERAGES ─────────────────────────────────────────────────────────
function computeAllMAs(hist) {
  const v = hist.map(h => h.v);
  const ma = (p) => v.map((_, i) => i < p - 1 ? null : v.slice(i - p + 1, i + 1).reduce((a, b) => a + b, 0) / p);
  return { ma50: ma(50), ma100: ma(100), ma200: ma(200) };
}

// ─── DELTA STRIKE INTERPOLATION ───────────────────────────────────────────────
function interpDeltaStrikes(dayOffset) {
  const S = MARKET.spot;
  if (dayOffset <= 0) return { p10: S, p25: S, c25: S, c10: S };
  const nodes = [{ days: 0, p10: S, p25: S, c25: S, c10: S }];
  const tenorDays = { '1M': 30, '3M': 91, '6M': 182, '1Y': 365 };
  for (const [tn, days] of Object.entries(tenorDays)) {
    const ds = DELTA_STRIKES[tn];
    if (ds) nodes.push({ days, ...ds });
  }
  nodes.sort((a, b) => a.days - b.days);
  if (dayOffset >= nodes[nodes.length - 1].days) {
    const last = nodes[nodes.length - 1];
    return { p10: last.p10, p25: last.p25, c25: last.c25, c10: last.c10 };
  }
  for (let i = 0; i < nodes.length - 1; i++) {
    if (dayOffset >= nodes[i].days && dayOffset <= nodes[i + 1].days) {
      const w = (dayOffset - nodes[i].days) / (nodes[i + 1].days - nodes[i].days);
      return {
        p10: nodes[i].p10 + w * (nodes[i + 1].p10 - nodes[i].p10),
        p25: nodes[i].p25 + w * (nodes[i + 1].p25 - nodes[i].p25),
        c25: nodes[i].c25 + w * (nodes[i + 1].c25 - nodes[i].c25),
        c10: nodes[i].c10 + w * (nodes[i + 1].c10 - nodes[i].c10),
      };
    }
  }
  return { p10: S, p25: S, c25: S, c10: S };
}

function buildChartData(history, allMAs, showMa, showFwd, showDeltas) {
  const lastDate = history[history.length - 1].d;
  const hist = history.map((h, i) => {
    const row = { d: h.d, spot: h.v };
    if (showMa.ma50 && h.ma50 != null) row.ma50 = h.ma50;
    if (showMa.ma100 && h.ma100 != null) row.ma100 = h.ma100;
    if (showMa.ma200 && h.ma200 != null) row.ma200 = h.ma200;
    return row;
  });
  if (!showFwd && !showDeltas) return hist;
  const fwdTenors = [{ k: '1W', d: 7 }, { k: '1M', d: 30 }, { k: '2M', d: 60 }, { k: '3M', d: 91 }, { k: '6M', d: 182 }, { k: '9M', d: 274 }, { k: '1Y', d: 365 }];
  const futureDates = [];
  const maxDays = 370;
  for (let d = 0; d <= maxDays; d += 5) futureDates.push({ d: addDays(lastDate, d), days: d });
  for (const ft of fwdTenors) {
    if (!futureDates.find(f => f.days === ft.d)) futureDates.push({ d: addDays(lastDate, ft.d), days: ft.d, tn: ft.k });
  }
  EVENTS.forEach(ev => {
    if (ev.date <= lastDate) return;
    const days = Math.round((new Date(ev.date) - new Date(lastDate)) / 864e5);
    if (days > 0 && days <= maxDays && !futureDates.find(f => f.d === ev.date))
      futureDates.push({ d: ev.date, days, ev });
  });
  futureDates.sort((a, b) => a.days - b.days);
  const seen = new Set();
  const uniqueFuts = futureDates.filter(f => { if (seen.has(f.d)) return false; seen.add(f.d); return true; });
  const fwdNodes = [{ days: 0, fwd: MARKET.spot }];
  for (const ft of fwdTenors) { const t = MARKET.tenors[ft.k]; if (t && t.fwd) fwdNodes.push({ days: ft.d, fwd: t.fwd }); }
  fwdNodes.sort((a, b) => a.days - b.days);
  const futs = uniqueFuts.map(f => {
    const row = { d: f.d };
    if (showFwd) {
      for (let i = 0; i < fwdNodes.length - 1; i++) {
        if (f.days >= fwdNodes[i].days && f.days <= fwdNodes[i + 1].days) {
          const w = (f.days - fwdNodes[i].days) / (fwdNodes[i + 1].days - fwdNodes[i].days);
          row.fwd = fwdNodes[i].fwd + w * (fwdNodes[i + 1].fwd - fwdNodes[i].fwd);
          break;
        }
      }
      if (row.fwd == null && f.days >= fwdNodes[fwdNodes.length - 1].days) row.fwd = fwdNodes[fwdNodes.length - 1].fwd;
    }
    if (f.tn) row.tn = f.tn;
    if (f.ev) row.ev = f.ev;
    if (showDeltas) {
      const ds = interpDeltaStrikes(f.days);
      row.fanBase = ds.p10;
      row.fanPutWing = ds.p25 - ds.p10;
      row.fanCore = ds.c25 - ds.p25;
      row.fanCallWing = ds.c10 - ds.c25;
    }
    return row;
  });
  const lastHist = hist[hist.length - 1];
  if (showDeltas && lastHist) {
    lastHist.fanBase = MARKET.spot; lastHist.fanPutWing = 0; lastHist.fanCore = 0; lastHist.fanCallWing = 0;
  }
  return [...hist, ...futs.filter(f => f.d > lastDate)];
}

// ─── SPOT CHART ───────────────────────────────────────────────────────────────
function SpotChart({ chartData, target, onDrag, spot, showDeltas, showDtcc, lastHistDate, rangeMode, rangeLo, rangeHi, onDragRange, expiryDate }) {
  const ref = useRef(null), [dragging, setDrag] = useState(false), [lvl, setLvl] = useState(null), [dragLine, setDragLine] = useState('target');
  const [hover, setHover] = useState(null);  // {idx, x, label} for the inline hover layer
  const allV = chartData.map(d => d.spot || d.fwd || d.ma50 || d.ma100 || d.ma200).filter(v => v != null && isFinite(v));
  if (typeof target === 'number' && isFinite(target)) allV.push(target);
  if (rangeMode) { if (typeof rangeLo === 'number' && isFinite(rangeLo)) allV.push(rangeLo); if (typeof rangeHi === 'number' && isFinite(rangeHi)) allV.push(rangeHi); }
  if (showDeltas) { const ds1y = DELTA_STRIKES['1Y']; if (ds1y) { allV.push(ds1y.p10, ds1y.c10); } }
  const mn = Math.min(...allV), mx = Math.max(...allV);
  const yMin = +(isFinite(mn) ? mn - .01 : .95).toFixed(3);
  const yMax = +(isFinite(mx) ? mx + .01 : 1.15).toFixed(3);
  const dom = [yMin, yMax > yMin ? yMax : yMin + .04];

  const getPoint = useCallback((e) => {
    const wrapper = ref.current;
    if (!wrapper) return null;
    const svg = wrapper.querySelector('.recharts-wrapper');
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    const plotTop = 10, plotBottom = rect.height - 55, plotH = plotBottom - plotTop;
    if (plotH <= 0) return null;
    const yPx = e.clientY - rect.top - plotTop;
    const ratio = 1 - yPx / plotH;
    const level = dom[0] + ratio * (dom[1] - dom[0]);
    if (!isFinite(level)) return null;
    const gridEl = wrapper.querySelector('.recharts-cartesian-grid');
    const gr = gridEl ? gridEl.getBoundingClientRect() : null;
    const plotLeft = gr ? gr.left : rect.left + 55, plotWidth = gr ? gr.width : rect.width - 65;
    const xRatio = Math.max(0, Math.min(1, (e.clientX - plotLeft) / plotWidth));
    const idx = Math.max(0, Math.min(chartData.length - 1, Math.round(xRatio * (chartData.length - 1))));
    const date = chartData[idx]?.d ?? null;
    return { level: Math.max(dom[0], Math.min(dom[1], +level.toFixed(4))), date, isFuture: date != null && lastHistDate != null && date > lastHistDate };
  }, [dom[0], dom[1], chartData, lastHistDate]);

  const down = e => {
    e.preventDefault();
    const p = getPoint(e);
    if (p != null) {
      let dl = 'target';
      if (rangeMode) {
        if (rangeLo != null && rangeHi != null) { const dLo = Math.abs(p.level - rangeLo), dHi = Math.abs(p.level - rangeHi); dl = dLo < dHi ? 'lo' : 'hi'; }
        else dl = rangeLo == null ? 'lo' : 'hi';
      }
      setDragLine(dl); setDrag(true); setLvl(p.level);
      if (rangeMode) onDragRange(dl, p.level);
      else onDrag(p.level, p.isFuture ? p.date : null);
    }
  };
  const move = e => { if (dragging) { e.preventDefault(); const p = getPoint(e); if (p != null) { setLvl(p.level); if (rangeMode) onDragRange(dragLine, p.level); } } };
  const up = () => { if (dragging && lvl != null) { if (!rangeMode) onDrag(lvl, null); } setDrag(false); };

  return (
    <div ref={ref} onMouseDown={down} onMouseMove={move} onMouseUp={up}
      onMouseLeave={() => { if (dragging) { if (!rangeMode && lvl != null) onDrag(lvl, null); setDrag(false); } }}
      style={{ position: 'relative', cursor: dragging ? 'ns-resize' : 'crosshair', userSelect: 'none' }}>
      {dragging && lvl != null && <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', zIndex: 10, background: rangeMode ? (dragLine === 'lo' ? C.bl : C.gn) : C.am, color: C.bg, padding: '3px 10px', borderRadius: 4, fontFamily: MONO, fontSize: 15, fontWeight: 700 }}>{rangeMode ? (dragLine === 'lo' ? '▼ ' : '▲ ') : ''}{safeFmt(lvl, 4)}</div>}
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={chartData} margin={{ top: 10, right: 10, bottom: 20, left: 10 }}
          onMouseMove={s => setHover(s && s.isTooltipActive && s.activeTooltipIndex != null
            ? { idx: s.activeTooltipIndex, x: s.activeCoordinate?.x, label: s.activeLabel } : null)}
          onMouseLeave={() => setHover(null)}>
          <defs>
            <linearGradient id="fxSG" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={C.ac} stopOpacity={.12} />
              <stop offset="95%" stopColor={C.ac} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={C.bd} />
          <XAxis dataKey="d" tick={{ fontSize: 9, fill: C.txD }} interval={Math.max(1, Math.floor(chartData.length / 14))} tickFormatter={v => typeof v === 'string' ? v.slice(2, 7).replace('-', '/') : ''} stroke={C.bd} angle={-30} textAnchor="end" height={35} />
          <YAxis domain={dom} tick={{ fontSize: 12, fill: C.txD }} stroke={C.bd} tickFormatter={v => safeFmt(v, 2)} width={48} allowDataOverflow />
          <Tooltip cursor={{ stroke: C.txM, strokeDasharray: '3 3', strokeOpacity: 0.45 }} content={() => null} isAnimationActive={false} />
          {showDeltas && <Area type="monotone" dataKey="fanBase"     stackId="fan" fill="none"     stroke="#79c0ff" strokeWidth={0.5} strokeOpacity={0.2} isAnimationActive={false} connectNulls={false} />}
          {showDeltas && <Area type="monotone" dataKey="fanPutWing"  stackId="fan" fill="#79c0ff" fillOpacity={0.07} stroke="#79c0ff" strokeWidth={1.5} strokeOpacity={0.35} isAnimationActive={false} connectNulls={false} />}
          {showDeltas && <Area type="monotone" dataKey="fanCore"     stackId="fan" fill="#79c0ff" fillOpacity={0.09} stroke="#79c0ff" strokeWidth={1.5} strokeOpacity={0.35} isAnimationActive={false} connectNulls={false} />}
          {showDeltas && <Area type="monotone" dataKey="fanCallWing" stackId="fan" fill="#79c0ff" fillOpacity={0.07} stroke="#79c0ff" strokeWidth={0.5} strokeOpacity={0.2} isAnimationActive={false} connectNulls={false} />}
          <Area type="monotone" dataKey="spot" stroke={C.ac} fill="url(#fxSG)" strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls={false} />
          <Line type="monotone" dataKey="fwd" stroke={C.am} strokeWidth={2} strokeDasharray="6 3"
            dot={props => { const { cx, cy, payload } = props; if (!payload.tn || !cx || !cy) return null; return <circle key={payload.tn} cx={cx} cy={cy} r={3} fill={C.am} stroke={C.bg} strokeWidth={1} />; }}
            isAnimationActive={false} connectNulls={false} />
          <Line type="monotone" dataKey="ma50"  stroke={MA_COLORS.ma50}  strokeWidth={1.2} dot={false} isAnimationActive={false} connectNulls={false} />
          <Line type="monotone" dataKey="ma100" stroke={MA_COLORS.ma100} strokeWidth={1.2} dot={false} isAnimationActive={false} connectNulls={false} />
          <Line type="monotone" dataKey="ma200" stroke={MA_COLORS.ma200} strokeWidth={1.2} dot={false} isAnimationActive={false} connectNulls={false} />
          {lastHistDate && <ReferenceLine x={lastHistDate} stroke={C.txM} strokeDasharray="2 2" label={{ value: 'TODAY', fill: C.txM, fontSize: 9, position: 'insideTopRight' }} />}
          {expiryDate && expiryDate > lastHistDate && <ReferenceLine key={`exp-${expiryDate}`} x={expiryDate} stroke={C.am} strokeDasharray="3 4" strokeOpacity={0.3} label={{ value: 'EXP', fill: C.am + '88', fontSize: 9, position: 'insideTopRight' }} />}
          {!rangeMode && typeof target === 'number' && isFinite(target) && <ReferenceLine y={target} stroke={C.am} strokeWidth={2} strokeDasharray="6 3" label={{ value: '◆ ' + safeFmt(target, 4), fill: C.am, fontSize: 13, fontWeight: 700, position: 'right' }} />}
          {rangeMode && typeof rangeLo === 'number' && isFinite(rangeLo) && <ReferenceLine y={rangeLo} stroke={C.gn} strokeWidth={2} strokeDasharray="6 3" label={{ value: '▼ ' + safeFmt(rangeLo, 4), fill: C.gn, fontSize: 12, fontWeight: 700, position: 'right' }} />}
          {rangeMode && typeof rangeHi === 'number' && isFinite(rangeHi) && <ReferenceLine y={rangeHi} stroke={C.pu} strokeWidth={2} strokeDasharray="6 3" label={{ value: '▲ ' + safeFmt(rangeHi, 4), fill: C.pu, fontSize: 12, fontWeight: 700, position: 'right' }} />}
          {rangeMode && typeof rangeLo === 'number' && typeof rangeHi === 'number' && <ReferenceArea y1={Math.min(rangeLo, rangeHi)} y2={Math.max(rangeLo, rangeHi)} fill={C.gn} fillOpacity={0.04} stroke="none" />}
          {dragging && lvl != null && <ReferenceLine y={lvl} stroke={rangeMode ? (dragLine === 'lo' ? C.gn : C.pu) : C.am} strokeWidth={1} strokeDasharray="2 2" strokeOpacity={.6} />}
        </ComposedChart>
      </ResponsiveContainer>
      {!dragging && hover && <ChartHoverLayer containerRef={ref} dom={dom} row={chartData[hover.idx]} hoverX={hover.x} label={hover.label}
        cfg={{ spotKey: 'spot', baseKey: 'fanBase', putWingKey: 'fanPutWing', coreKey: 'fanCore', callWingKey: 'fanCallWing', fmt: v => v.toFixed(4) }} />}
      {showDtcc && <DtccBubbles containerRef={ref} dom={dom} chartData={chartData} lastHistDate={lastHistDate} />}
      <div style={{ fontSize: 9, color: C.txM, textAlign: 'center', marginTop: 1 }}>
        {rangeMode ? 'Drag to set range bounds · click future date to set expiry' : 'Drag vertically to set target · click future date to set expiry'}
      </div>
    </div>
  );
}

// ─── DTCC BUBBLES ─────────────────────────────────────────────────────────────
function DtccBubbles({ containerRef, dom, chartData, lastHistDate }) {
  const [hover, setHover] = useState(null);
  const data = useMemo(() => DTCC_DATA.filter(d => d.n > 20 && d.K > 0.85 && d.K < 1.25), []);
  const dates = useMemo(() => chartData.map(d => d.d), [chartData]);
  const dateToFrac = (date) => {
    const idx = dates.indexOf(date);
    if (idx >= 0) return idx / (dates.length - 1);
    for (let i = 0; i < dates.length - 1; i++) { if (dates[i] < date && dates[i + 1] >= date) return (i + 0.5) / (dates.length - 1); }
    return date < dates[0] ? 0 : 1;
  };
  if (!data.length) return null;
  const t3m = MARKET.tenors['3M'];
  const deltaAlpha = (d) => {
    const sig = interpVol(t3m.points, d.K);
    const delta = gkDelta(MARKET.spot, d.K, t3m.T, t3m.rd, t3m.rf, sig, d.c);
    const absDelta = Math.abs(delta) * 100;
    if (absDelta >= 50) return 1.0;
    if (absDelta <= 10) return 0.1;
    return 0.1 + (absDelta - 10) / 40 * 0.9;
  };
  const plotLeft = 58, plotTop = 10;
  const getPlotDims = () => {
    const el = containerRef.current;
    if (!el) return null;
    const wrapper = el.querySelector('.recharts-wrapper');
    if (!wrapper) return null;
    const rect = wrapper.getBoundingClientRect();
    return { w: rect.width - plotLeft - 10, h: rect.height - plotTop - 55, fullW: rect.width, fullH: rect.height };
  };
  const dims = getPlotDims();
  if (!dims || dims.w <= 0 || dims.h <= 0) return null;
  const yRange = dom[1] - dom[0];
  return (
    <div style={{ position: 'absolute', top: 0, left: 0, width: dims.fullW, height: dims.fullH, pointerEvents: 'none' }}>
      {data.map((d, i) => {
        const xFrac = dateToFrac(d.d);
        const x = plotLeft + xFrac * dims.w;
        const y = plotTop + (1 - (d.K - dom[0]) / yRange) * dims.h;
        if (y < plotTop - 5 || y > plotTop + dims.h + 5 || x < plotLeft - 5 || x > plotLeft + dims.w + 5) return null;
        const r = Math.max(2, Math.min(20, Math.sqrt(d.n / 500) * 6));
        const isHovered = hover === i;
        const alpha = deltaAlpha(d);
        return (
          <div key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
            style={{ position: 'absolute', left: x - r, top: y - r, width: r * 2, height: r * 2, borderRadius: '50%', background: d.c ? C.bl : C.gn, opacity: isHovered ? Math.min(1, alpha + 0.15) : alpha, border: isHovered ? `2px solid ${d.c ? C.bl : C.gn}` : 'none', pointerEvents: 'all', cursor: 'pointer', transition: 'opacity .1s', transform: isHovered ? 'scale(1.2)' : 'none', zIndex: isHovered ? 20 : 1 }} />
        );
      })}
      {hover !== null && data[hover] && (() => {
        const d = data[hover];
        const xFrac = dateToFrac(d.d);
        const x = plotLeft + xFrac * dims.w;
        const y = plotTop + (1 - (d.K - dom[0]) / yRange) * dims.h;
        const r = Math.max(2, Math.min(20, Math.sqrt(d.n / 500) * 6));
        return (
          <div style={{ position: 'absolute', left: x + r + 6, top: y - 40, background: C.bgC, border: `1px solid ${C.bd}`, borderRadius: 6, padding: '6px 10px', fontSize: 12, color: C.tx, pointerEvents: 'none', zIndex: 30, whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>
            <div style={{ fontWeight: 700, color: d.c ? C.bl : C.gn, marginBottom: 2, fontSize: 13 }}>{d.c ? 'CALL' : 'PUT'}</div>
            <div><span style={{ color: C.txD }}>Expiry </span><span style={{ fontFamily: MONO, fontWeight: 600 }}>{d.d}</span></div>
            <div><span style={{ color: C.txD }}>Strike </span><span style={{ fontFamily: MONO, fontWeight: 600 }}>{safeFmt(d.K, 4)}</span></div>
            <div><span style={{ color: C.txD }}>Notional </span><span style={{ fontFamily: MONO, fontWeight: 600 }}>{d.n >= 1000 ? (d.n / 1000).toFixed(1) + 'B' : d.n.toFixed(0) + 'M'}</span></div>
          </div>
        );
      })()}
    </div>
  );
}

// ─── PAYOFF CHART ─────────────────────────────────────────────────────────────
function PayoffChart({ data, target, spot, density, rangeLo, rangeHi }) {
  if (!data || !data.length) return null;
  const pnls = data.map(d => d.pnl).filter(v => isFinite(v));
  if (!pnls.length) return null;
  const yMin = Math.min(...pnls), yMax = Math.max(...pnls), pad = Math.max((yMax - yMin) * .15, .002);
  const pD = [isFinite(yMin - pad) ? yMin - pad : -.01, isFinite(yMax + pad) ? yMax + pad : .01];
  if (pD[0] >= pD[1]) pD[1] = pD[0] + .01;
  const merged = data.map(d => {
    let prob = 0;
    if (density && density.Ks && density.pdf) {
      for (let i = 0; i < density.Ks.length - 1; i++) {
        if (density.Ks[i] <= d.spot && density.Ks[i + 1] >= d.spot) {
          const w = (d.spot - density.Ks[i]) / (density.Ks[i + 1] - density.Ks[i]);
          prob = density.pdf[i] + w * (density.pdf[i + 1] - density.pdf[i]);
          break;
        }
      }
    }
    return { ...d, pnl: isFinite(d.pnl) ? d.pnl : 0, prob: isFinite(prob) ? prob : 0 };
  });
  const mxP = Math.max(...merged.map(m => m.prob), .001);
  return (
    <ResponsiveContainer width="100%" height={180}>
      <ComposedChart data={merged} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
        <defs>
          <linearGradient id="fxPG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.gn} stopOpacity={.25} /><stop offset="95%" stopColor={C.gn} stopOpacity={0} /></linearGradient>
          <linearGradient id="fxDG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.pu} stopOpacity={.2} /><stop offset="95%" stopColor={C.pu} stopOpacity={0} /></linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={C.bd} />
        <XAxis dataKey="spot" tick={{ fontSize: 11, fill: C.txD }} tickFormatter={v => safeFmt(v, 2)} stroke={C.bd} />
        <YAxis yAxisId="pnl" domain={pD} tick={{ fontSize: 11, fill: C.txD }} tickFormatter={v => typeof v === 'number' && isFinite(v) ? (v * 1e4).toFixed(0) + 'p' : ''} width={42} stroke={C.bd} />
        <YAxis yAxisId="prob" orientation="right" domain={[0, isFinite(mxP) ? mxP * 1.2 : .01]} tick={{ fontSize: 10, fill: C.pu + '77' }} tickFormatter={v => typeof v === 'number' && isFinite(v) ? (v * 100).toFixed(0) + '%' : ''} width={32} stroke={C.bd} />
        <Tooltip contentStyle={{ background: C.bgC, border: `1px solid ${C.bd}`, borderRadius: 6, fontSize: 12, color: C.tx }} labelFormatter={l => typeof l === 'number' ? 'Spot: ' + l.toFixed(4) : 'Spot: ' + l} formatter={(v, n) => { if (typeof v !== 'number' || !isFinite(v)) return ['–', n]; return n === 'pnl' ? [(v * 1e4).toFixed(1) + ' pips', 'P&L'] : [(v * 100).toFixed(2) + '%', 'Density']; }} />
        <ReferenceLine yAxisId="pnl" y={0} stroke={C.txM} />
        {typeof target === 'number' && isFinite(target) && <ReferenceLine yAxisId="pnl" x={target} stroke={C.am} strokeDasharray="4 4" />}
        {typeof rangeLo === 'number' && typeof rangeHi === 'number' && <ReferenceArea yAxisId="pnl" x1={Math.min(rangeLo, rangeHi)} x2={Math.max(rangeLo, rangeHi)} fill={C.gn} fillOpacity={0.05} />}
        {typeof rangeLo === 'number' && <ReferenceLine yAxisId="pnl" x={rangeLo} stroke={C.gn} strokeDasharray="4 4" strokeOpacity={0.7} />}
        {typeof rangeHi === 'number' && <ReferenceLine yAxisId="pnl" x={rangeHi} stroke={C.pu} strokeDasharray="4 4" strokeOpacity={0.7} />}
        <Area yAxisId="prob" type="monotone" dataKey="prob" stroke={C.pu} fill="url(#fxDG)" strokeWidth={1} dot={false} strokeOpacity={.6} isAnimationActive={false} />
        <Area yAxisId="pnl"  type="monotone" dataKey="pnl"  stroke={C.gn} fill="url(#fxPG)" strokeWidth={2} dot={false} isAnimationActive={false} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

// ─── FX EXCLUSION ROW ─────────────────────────────────────────────────────────
// Variant of ExclusionRow with spot-price formatting (4 dp) instead of bp.
function FxExclusionRow({ excl, idx, onUpdate, onRemove }) {
  const [levelStr, setLevelStr] = useState(() => excl.level.toFixed(4));
  const [confStr,  setConfStr]  = useState(() => (excl.confidence * 100).toFixed(0));
  useEffect(() => { setLevelStr(excl.level.toFixed(4)); }, [excl.level]);
  useEffect(() => { setConfStr((excl.confidence * 100).toFixed(0)); }, [excl.confidence]);
  const commitLevel = () => {
    const v = parseFloat(levelStr);
    if (!isNaN(v) && v > 0.5 && v < 2.5) onUpdate(idx, 'level', v);
    else setLevelStr(excl.level.toFixed(4));
  };
  const commitConf = () => {
    const v = parseFloat(confStr) / 100;
    if (!isNaN(v) && v > 0 && v < 0.5) onUpdate(idx, 'confidence', v);
    else setConfStr((excl.confidence * 100).toFixed(0));
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: C.bgH, border: `1px solid ${C.bd}`, borderRadius: 3, padding: '3px 6px' }}>
      <button onClick={() => onUpdate(idx, 'side', excl.side === 'above' ? 'below' : 'above')}
        style={{ background: excl.side === 'above' ? C.rdD : C.gnD, color: excl.side === 'above' ? C.rd : C.gn, border: 'none', borderRadius: 2, padding: '1px 5px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: MONO }}>
        {excl.side === 'above' ? '↑' : '↓'}
      </button>
      <span style={{ fontSize: 10, color: C.txD }}>won't reach</span>
      <input type="text" value={levelStr} onChange={ev => setLevelStr(ev.target.value)} onBlur={commitLevel} onKeyDown={ev => { if (ev.key === 'Enter') ev.target.blur(); }}
        style={{ background: C.bg, border: `1px solid ${C.bd}`, borderRadius: 2, padding: '2px 4px', color: C.tx, fontSize: 12, width: 64, fontFamily: MONO, outline: 'none' }} />
      <span style={{ fontSize: 10, color: C.txD }}>@</span>
      <input type="text" value={confStr} onChange={ev => setConfStr(ev.target.value)} onBlur={commitConf} onKeyDown={ev => { if (ev.key === 'Enter') ev.target.blur(); }}
        style={{ background: C.bg, border: `1px solid ${C.bd}`, borderRadius: 2, padding: '2px 4px', color: C.tx, fontSize: 12, width: 28, fontFamily: MONO, outline: 'none' }} />
      <span style={{ fontSize: 10, color: C.txD }}>%</span>
      <button onClick={() => onRemove(idx)} style={{ background: 'transparent', color: C.rd, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700, padding: '0 2px', lineHeight: 1 }}>×</button>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
const DATA_SOURCES = [
  { name: "Bloomberg", id: "bbg",     status: "demo", desc: "blpapi: full vol surface" },
  { name: "Yahoo",     id: "yahoo",   status: "ok",   desc: "Free spot rates" },
  { name: "Polygon",   id: "polygon", status: "key",  desc: "Free FX spot" },
  { name: "ECB",       id: "ecb",     status: "ok",   desc: "Official daily EUR rates" },
];

export default function FxApp() {
  const [target,     setTarget]     = useState(null);
  const [inp,        setInp]        = useState('');
  const [tenor,      setTenor]      = useState('6M');
  const [zeroCost,   setZC]         = useState(false);
  const [idx,        setIdx]        = useState(0);
  const [rank,       setRank]       = useState('combined');
  const [evFilt,     setEvFilt]     = useState(null);
  const [showDS,     setShowDS]     = useState(false);
  const [showMa,     setShowMa]     = useState({ ma50: false, ma100: false, ma200: false });
  const [showDeltas, setShowDeltas] = useState(false);
  const [showDtcc,   setShowDtcc]   = useState(false);
  const [showFwd,    setShowFwd]    = useState(true);
  const [histPeriod, setHistPeriod] = useState('1Y');
  const [mode,       setMode]       = useState('target');
  const [rangeLo,    setRangeLo]    = useState(null);
  const [rangeHi,    setRangeHi]    = useState(null);
  const [inpLo,      setInpLo]      = useState('');
  const [inpHi,      setInpHi]      = useState('');
  const [confidence, setConfidence] = useState(0.5);
  const [exclusions, setExclusions] = useState([]);
  const [riskBudget, setRiskBudget] = useState(10);
  const [customExpDate, setCustomExpDate] = useState('');
  const [expDateLocked, setExpDateLocked] = useState(false);

  const spot = MARKET.spot;
  const dir = target ? (target > spot ? 'BULL' : 'BEAR') : null;

  const allMAs = useMemo(() => computeAllMAs(HISTORY_FULL), []);
  const displayHistory = useMemo(() => {
    const n = HIST_PERIODS[histPeriod] || 252;
    const startIdx = Math.max(0, HISTORY_FULL.length - n);
    return HISTORY_FULL.slice(startIdx).map((h, i) => {
      const gi = startIdx + i;
      const row = { ...h };
      if (showMa.ma50  && allMAs.ma50[gi]  != null) row.ma50  = allMAs.ma50[gi];
      if (showMa.ma100 && allMAs.ma100[gi] != null) row.ma100 = allMAs.ma100[gi];
      if (showMa.ma200 && allMAs.ma200[gi] != null) row.ma200 = allMAs.ma200[gi];
      return row;
    });
  }, [histPeriod, showMa, allMAs]);

  const chartData = useMemo(() => buildChartData(displayHistory, allMAs, showMa, showFwd, showDeltas), [displayHistory, allMAs, showMa, showFwd, showDeltas]);
  const lastHistDate = displayHistory[displayHistory.length - 1]?.d;
  // Date corresponding to the selected standard tenor — keeps the date picker synchronised.
  const tenorExpiryDate = lastHistDate ? addDays(lastHistDate, TENOR_DAYS[tenor] || 0) : '';

  // Build custom tenor when custom expiry date is set
  const dCache = useMemo(() => {
    const c = {};
    TENOR_ORDER.forEach(t => { if (MARKET.tenors[t]) c[t] = computeRNDensity(t); });
    return c;
  }, []);

  const results = useMemo(() => {
    const anchor = HISTORY_FULL[HISTORY_FULL.length - 1]?.d;
    if (customExpDate && anchor) {
      const T_req = Math.max(1 / 52, (new Date(customExpDate) - new Date(anchor)) / (365.25 * 86400000));
      const synth = buildCustomFxTenor(T_req);
      if (synth) { MARKET.tenors['custom'] = synth; dCache['custom'] = computeRNDensity('custom'); }
    }
    const effectiveTenors = customExpDate ? ['custom'] : [tenor];
    if (mode === 'target') return target ? optimize(target, effectiveTenors, zeroCost, rank, dCache, exclusions, riskBudget) : [];
    if (mode === 'range' && rangeLo != null && rangeHi != null)
      return optimizeRange(Math.min(rangeLo, rangeHi), Math.max(rangeLo, rangeHi), effectiveTenors, zeroCost, rank, dCache, exclusions, riskBudget);
    return [];
  }, [mode, target, rangeLo, rangeHi, tenor, zeroCost, rank, dCache, exclusions, riskBudget, customExpDate]);

  const sel = results[idx] || null;

  const goTarget = () => { const v = parseFloat(inp); if (v > .5 && v < 2) { setTarget(v); setIdx(0); } };
  const goRange  = () => {
    const lo = parseFloat(inpLo), hi = parseFloat(inpHi);
    if (lo > .5 && lo < 2 && hi > .5 && hi < 2) { setRangeLo(Math.min(lo, hi)); setRangeHi(Math.max(lo, hi)); setIdx(0); }
  };

  const onDrag = useCallback((l, clickDate) => {
    setTarget(l); setInp(l.toFixed(4)); setIdx(0);
    if (clickDate && !expDateLocked) {
      setCustomExpDate(clickDate);
    }
  }, [expDateLocked]);

  const onDragRange = useCallback((which, l) => {
    if (which === 'lo') { setRangeLo(l); setInpLo(l.toFixed(4)); }
    else { setRangeHi(l); setInpHi(l.toFixed(4)); }
    setIdx(0);
  }, []);

  const togEv    = ev => { if (evFilt && evFilt.date === ev.date) setEvFilt(null); else setEvFilt({ date: ev.date, name: ev.name, mode: 'after' }); setIdx(0); };
  const togMa    = k => setShowMa(p => ({ ...p, [k]: !p[k] }));

  const addExclusion = side => {
    const defaultLevel = side === 'above' ? spot * 1.05 : spot * 0.95;
    if (exclusions.length < 2) setExclusions(p => [...p, { side, level: defaultLevel, confidence: 0.12 }]);
    setIdx(0);
  };
  // Signature matches ExclusionRow: onUpdate(idx, field, val)
  const updateExclusion = (i, field, val) => { setExclusions(p => p.map((e, j) => j === i ? { ...e, [field]: val } : e)); setIdx(0); };
  const removeExclusion = i => { setExclusions(p => p.filter((_, j) => j !== i)); setIdx(0); };

  // Expiry date for the selected strategy's chart line
  const selExpDate = sel && lastHistDate
    ? (sel.legs[0]?.tenor === 'custom' ? customExpDate : addDays(lastHistDate, TENOR_DAYS[sel.legs[0]?.tenor] || 0))
    : (customExpDate || null);

  return (
    <div style={S.page}>
      <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap" rel="stylesheet" />
      <style>{`* { box-sizing: border-box; } body { margin: 0; } button:hover { filter: brightness(1.15); } .mono { font-family: 'IBM Plex Mono', monospace; }`}</style>

      {/* HEADER */}
      <div style={S.header}>
        <div style={S.headerLeft}>
          <div style={logoIcon(C.ac, C.pu)}>F</div>
          <div>
            <div style={S.pageTitle}>TANGOALPHA FX</div>
            <div style={S.pageSub}>EUR/USD Options Strategy Optimizer</div>
          </div>
        </div>
        <div style={S.headerRight}>
          <button onClick={() => setShowDS(!showDS)} style={S.dataBtn}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: C.gn }} />DATA
          </button>
          <div style={S.asOf}>AS OF <span className="mono" style={{ color: C.txD }}>{AS_OF}</span></div>
          <div style={{ textAlign: 'right' }}>
            <div style={S.spotLabel}>EUR/USD</div>
            <div className="mono" style={spotNum(C.ac)}>{spot.toFixed(4)}</div>
          </div>
          {mode === 'target' && dir && (
            <div style={badge(dir === 'BULL' ? C.gn : C.rd)}>
              {dir} → <span className="mono">{target?.toFixed(4)}</span>{' '}({((target / spot - 1) * 100).toFixed(1)}%)
            </div>
          )}
          {mode === 'range' && rangeLo != null && rangeHi != null && (
            <div style={badge(C.bl)}>
              RANGE <span className="mono">{Math.min(rangeLo, rangeHi).toFixed(4)} – {Math.max(rangeLo, rangeHi).toFixed(4)}</span>
            </div>
          )}
        </div>
      </div>

      {showDS && (
        <div style={S.dsRow}>
          {DATA_SOURCES.map(ds => (
            <div key={ds.id} style={S.dsChip}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: ds.status === 'ok' ? C.gn : ds.status === 'demo' ? C.am : C.txM }} />
              <span style={{ fontWeight: 600, color: C.tx }}>{ds.name}</span>
              <span style={{ color: C.txD }}>{ds.desc}</span>
            </div>
          ))}
        </div>
      )}

      <div style={S.pageContent}>
        {/* LEFT COLUMN */}
        <div style={{ borderRight: `1px solid ${C.bd}`, display: 'flex', flexDirection: 'column' }}>

          {/* Chart header */}
          <div style={{ ...S.strip, justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={S.label}>Underlying</span>
              <select style={{ ...S.select, padding: '2px 6px', fontSize: F.xs }}>
                <option value="EUR/USD">EUR/USD</option>
              </select>
              <div style={S.divider} />
              <span style={S.label}>Range</span>
              {Object.keys(HIST_PERIODS).map(p => (
                <button key={p} onClick={() => setHistPeriod(p)} style={btn(histPeriod === p, C.bl, C.blD)}>{p}</button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              <button onClick={() => setShowDeltas(!showDeltas)} style={btn(showDeltas, C.rd, 'rgba(248,113,113,.1)')}>Δ CONES</button>
              <button onClick={() => setShowFwd(!showFwd)} style={btn(showFwd, C.am, C.amD)}>FWDS</button>
              <button onClick={() => setShowDtcc(!showDtcc)} style={btn(showDtcc, C.gn, 'rgba(63,185,80,.12)')}>DTCC</button>
              {[['ma50', '50d', MA_COLORS.ma50], ['ma100', '100d', MA_COLORS.ma100], ['ma200', '200d', MA_COLORS.ma200]].map(([k, l, c]) => (
                <button key={k} onClick={() => togMa(k)} style={btn(showMa[k], c, c + '22')}>{l}</button>
              ))}
            </div>
          </div>

          {/* Spot chart */}
          <div style={{ padding: '8px 14px', borderBottom: `1px solid ${C.bd}` }}>
            <SpotChart chartData={chartData} target={mode === 'target' ? target : null} spot={spot}
              onDrag={onDrag} showDeltas={showDeltas} showDtcc={showDtcc}
              lastHistDate={lastHistDate} rangeMode={mode === 'range'}
              rangeLo={rangeLo} rangeHi={rangeHi} onDragRange={onDragRange}
              expiryDate={selExpDate} />
          </div>

          {/* VIEW + EXPIRY + RISK BUDGET */}
          <div style={S.strip}>
            <span style={S.label}>View</span>
            {[['target', 'TARGET'], ['range', 'RANGE']].map(([m, l]) => (
              <button key={m} onClick={() => { setMode(m); setIdx(0); }} style={btn(mode === m)}>{l}</button>
            ))}
            <div style={{ width: 1, height: 16, background: C.bd, margin: '0 4px' }} />
            {mode === 'target' && <>
              <label style={S.label}>Target</label>
              <input type="text" value={inp} onChange={e => setInp(e.target.value)} onKeyDown={e => e.key === 'Enter' && goTarget()} onBlur={goTarget} placeholder="1.10"
                style={{ ...S.input, width: 74 }} />
            </>}
            {mode === 'range' && <>
              <label style={{ ...S.label, color: C.gn }}>Lo</label>
              <input type="text" value={inpLo} onChange={e => setInpLo(e.target.value)} onKeyDown={e => e.key === 'Enter' && goRange()} onBlur={goRange} placeholder="1.05"
                style={{ ...S.input, width: 68 }} />
              <label style={{ ...S.label, color: C.pu }}>Hi</label>
              <input type="text" value={inpHi} onChange={e => setInpHi(e.target.value)} onKeyDown={e => e.key === 'Enter' && goRange()} onBlur={goRange} placeholder="1.12"
                style={{ ...S.input, width: 68 }} />
            </>}
            <div style={S.divider} />
            <span style={S.label}>Exp</span>
            {TENOR_ORDER.map(t => (
              <button key={t} onClick={() => { setTenor(t); setCustomExpDate(''); setIdx(0); }}
                style={btn(!customExpDate && tenor === t, C.ac, C.acD)}>
                {t}
              </button>
            ))}
            <div style={{ width: 1, height: 14, background: C.bd, margin: '0 2px' }} />
            <input type="date" value={customExpDate || tenorExpiryDate}
              min={lastHistDate ? addDays(lastHistDate, 1) : ''}
              max={lastHistDate ? addDays(lastHistDate, 400) : ''}
              onChange={e => setCustomExpDate(e.target.value)}
              style={{ fontSize: F.sm, background: customExpDate ? C.acD : C.bg, color: customExpDate ? C.ac : C.txD, border: `1px solid ${customExpDate ? C.ac : C.bd}`, borderRadius: 2, padding: '2px 4px', fontFamily: MONO, colorScheme: 'dark', cursor: 'pointer' }} />
            {customExpDate && <button onClick={() => setCustomExpDate('')} style={{ background: 'transparent', border: 'none', color: C.txD, cursor: 'pointer', fontSize: F.md, padding: '0 2px' }}>&#x2715;</button>}
            <button onClick={() => setExpDateLocked(l => !l)}
              title={expDateLocked ? 'Unlock: chart clicks will update the expiry date' : 'Lock: chart clicks won\'t change the expiry date'}
              style={{ background: expDateLocked ? C.acD : 'transparent', border: `1px solid ${expDateLocked ? C.ac : C.bd}`, borderRadius: 2, padding: '2px 5px', color: expDateLocked ? C.ac : C.txD, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              {expDateLocked
                ? <svg width="12" height="14" viewBox="0 0 11 13" fill="none"><rect x="1" y="5.5" width="9" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M3.5 5.5V3.5a2 2 0 0 1 4 0V5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                : <svg width="12" height="14" viewBox="0 0 11 13" fill="none"><rect x="1" y="5.5" width="9" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M3.5 5.5V3.5a2 2 0 0 1 4 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
              }
            </button>
            <div style={S.divider} />
            <label style={S.label}>R</label>
            <input type="number" value={riskBudget} min="1" max="200"
              onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v) && v > 0) { setRiskBudget(v); setIdx(0); } }}
              style={{ ...S.inputSm, width: 44 }} />
            <span style={S.label}>pips</span>
            <div style={{ flex: 1 }} />
            <label style={S.label}>Conf</label>
            <input type="range" min="0.2" max="0.9" step="0.05" value={confidence}
              onChange={e => { setConfidence(parseFloat(e.target.value)); setIdx(0); }}
              style={{ width: 70, accentColor: C.ac }} />
            <span className="mono" style={{ fontSize: F.md, color: C.ac, fontWeight: 700, width: 30 }}>{(confidence * 100).toFixed(0)}%</span>
          </div>

          {/* EXCLUSIONS */}
          <div style={{ ...S.strip, gap: 10 }}>
            <span style={S.label}>Exclusions</span>
            <span style={{ fontSize: F.xxs, color: C.txM, fontStyle: 'italic' }}>levels you don't think EUR/USD will reach — unlocks risk reversals</span>
            {exclusions.map((e, i) => (
              <FxExclusionRow key={i} idx={i} excl={e} onUpdate={updateExclusion} onRemove={removeExclusion} />
            ))}
            {exclusions.length < 2 && <>
              <button onClick={() => addExclusion('above')} style={{ ...S.btnGhost, border: `1px dashed ${C.bd}`, color: C.rd }}>+ ↑ above</button>
              <button onClick={() => addExclusion('below')} style={{ ...S.btnGhost, border: `1px dashed ${C.bd}`, color: C.gn }}>+ ↓ below</button>
            </>}
          </div>

          {/* RANK + filters */}
          <div style={{ ...S.strip, gap: 5 }}>
            <span style={{ ...S.label, color: C.txM }}>Rank</span>
            {[['bfb', 'Bang/Buck'], ['probability', 'P-weighted'], ['combined', 'Combined']].map(([m, l]) => (
              <button key={m} onClick={() => { setRank(m); setIdx(0); }} style={btn(rank === m)}>{l}</button>
            ))}
            <div style={{ flex: 1 }} />
            <button onClick={() => { setZC(!zeroCost); setIdx(0); }} style={btn(zeroCost, C.am, 'rgba(210,153,34,.15)')}>ZERO COST</button>
            {evFilt && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 2, background: 'rgba(188,140,255,.1)', border: `1px solid ${C.pu}40`, borderRadius: 3, padding: '2px 6px' }}>
                <span style={{ fontSize: 10, color: C.pu, fontWeight: 600 }}>{evFilt.mode === 'after' ? 'After' : 'Before'}: {evFilt.name}</span>
                <button onClick={() => setEvFilt(null)} style={{ background: 'none', border: 'none', color: C.pu, cursor: 'pointer', fontSize: 11, padding: 0 }}>&#x2715;</button>
                <button onClick={() => setEvFilt(f => ({ ...f, mode: f.mode === 'after' ? 'before' : 'after' }))} style={{ background: 'none', border: `1px solid ${C.pu}40`, borderRadius: 2, color: C.pu, cursor: 'pointer', fontSize: 6, padding: '1px 3px' }}>flip</button>
              </div>
            )}
          </div>

          {/* "At R" label strip */}
          <div style={{ padding: '4px 14px', borderBottom: `1px solid ${C.bd}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: F.xs, color: C.txM, background: C.bg }}>
            <span style={{ textTransform: 'uppercase', letterSpacing: '.08em' }}>
              Amounts scaled to R = <span className="mono" style={{ color: C.ac, fontWeight: 700 }}>{riskBudget} pips</span>
            </span>
            <span style={{ fontStyle: 'italic' }}>size x per-unit shown in detail panel</span>
          </div>

          {/* Results table */}
          <div style={{ flex: 1, overflow: 'auto' }}>
            {results.length === 0
              ? <div style={{ padding: 30, textAlign: 'center', color: C.txD, fontSize: 13 }}>
                  {mode === 'target'
                    ? (target ? 'No strategies -- adjust filters' : 'Drag chart or enter target, click future date to set expiry')
                    : (rangeLo != null && rangeHi != null ? 'No strategies -- adjust filters' : 'Drag chart to set range bounds')}
                </div>
              : <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: F.md }}>
                  <thead style={{ background: C.bgC, position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr style={{ borderBottom: `1px solid ${C.bd}` }}>
                      {[['Structure','left'],['Exp','right'],['Prem','right'],[mode==='range'?'@Avg':'@Tgt','right'],['B/B','right'],['P(win)','right'],['Size','right'],['MaxL','right']].map(([h, a]) => (
                        <th key={h} style={{ ...S.th, textAlign: a, padding: '8px 8px' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r, i) => (
                      <tr key={i} onClick={() => setIdx(i)}
                        style={{ borderBottom: `1px solid ${C.bd}`, cursor: 'pointer', background: i === idx ? C.bgH : 'transparent' }}>
                        <td style={{ padding: '7px 8px', color: i === idx ? C.ac : C.tx, fontWeight: i === idx ? 600 : 500, fontSize: F.sm }}>
                          {r.archetype}
                        </td>
                        <td style={{ textAlign: 'right', padding: '7px 6px', fontFamily: MONO, fontSize: F.sm, color: C.txD }}>
                          {r.legs[0] && r.legs[0].tenor === 'custom' ? customExpDate && customExpDate.slice(5) : r.legs[0] && r.legs[0].tenor}
                        </td>
                        <td style={{ textAlign: 'right', padding: '7px 6px', fontFamily: MONO, fontSize: F.sm, color: r.premPct > 0 ? C.rd : C.gn }}>
                          {r.premPct > 0 ? '-' : '+'}{Math.abs(r.premPct).toFixed(2)}%
                        </td>
                        <td style={{ textAlign: 'right', padding: '7px 6px', fontFamily: MONO, fontSize: F.sm, color: C.gn, fontWeight: 600 }}>
                          {mode === 'range' ? (r.avgRangePnl * 1e4).toFixed(0) : (r.targetPnl * 1e4).toFixed(0)}p
                        </td>
                        <td style={{ textAlign: 'right', padding: '7px 6px', fontFamily: MONO, fontSize: F.sm, color: C.am, fontWeight: 600 }}>
                          {r.bfb.toFixed(1)}x
                        </td>
                        <td style={{ textAlign: 'right', padding: '7px 6px', fontFamily: MONO, fontSize: F.sm, color: C.bl }}>
                          {((r.pp || 0) * 100).toFixed(0)}%
                        </td>
                        <td style={{ textAlign: 'right', padding: '7px 6px', fontFamily: MONO, fontSize: F.sm, color: C.bl }}>
                          {r.size.toFixed(2)}x
                        </td>
                        <td style={{ textAlign: 'right', padding: '7px 8px', fontFamily: MONO, fontSize: F.sm, color: C.rd }}>
                          {(r.maxLoss * 1e4).toFixed(0)}p
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
            }
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div style={{ ...S.rightPanel, maxHeight: `calc(100vh - ${HEADER_H}px)` }}>
          {sel ? <>
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.bd}` }}>
              <div style={{ ...S.label, marginBottom: 4 }}>Selected #{idx + 1}</div>
              <div style={{ fontSize: F.xl, fontWeight: 600, color: C.ac, marginBottom: 2 }}>{sel.archetype}</div>
              <div style={{ fontSize: F.sm, color: C.txD }}>
                <span className="mono">{sel.legs[0] && sel.legs[0].tenor === 'custom' ? customExpDate : sel.legs[0] && sel.legs[0].tenor}</span>
                {' EUR/USD · ATM '}
                <span className="mono">{spot.toFixed(4)}</span>
              </div>
            </div>

            <div style={{ padding: '10px 16px', borderBottom: `1px solid ${C.bd}` }}>
              <div style={{ ...S.label, marginBottom: 6 }}>Legs</div>
              {sel.legs.map((l, i) => {
                const p = priceOpt(l.K, l.tenor, l.isCall);
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', fontSize: F.sm, borderTop: i > 0 ? `1px dashed ${C.bd}` : 'none' }}>
                    <div>
                      <span style={{ color: l.dir > 0 ? C.gn : C.rd, fontWeight: 700, marginRight: 6, fontFamily: MONO }}>
                        {l.dir > 0 ? '+' : '-'}{l.ratio !== 1 ? l.ratio + 'x' : ''}
                      </span>
                      <span style={{ color: l.isCall ? C.bl : C.gn, fontWeight: 600 }}>{l.isCall ? 'Call' : 'Put'}</span>
                      <span style={{ color: C.txD, marginLeft: 4 }} className="mono">K={l.K.toFixed(4)}</span>
                    </div>
                    <div style={{ textAlign: 'right', color: C.txD, fontSize: F.sm }} className="mono">
                      vol={(p.vol * 100).toFixed(2)}% prem={(p.premium * 1e4).toFixed(1)}p
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ padding: '10px 16px', borderBottom: `1px solid ${C.bd}`, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', fontSize: F.sm }}>
              {[
                ['Net Debit', (sel.premPct > 0 ? '-' : '+') + Math.abs(sel.premPct).toFixed(2) + '%', sel.premPct > 0 ? C.rd : C.gn],
                ['Size (at R)', sel.size.toFixed(2) + 'x', C.bl],
                [mode === 'range' ? '@Avg' : '@Target', ((mode === 'range' ? sel.avgRangePnl : sel.targetPnl) * 1e4).toFixed(0) + 'p', C.gn],
                ['Max Gain', sel.maxGain > 0.05 ? 'Unlimited' : (sel.maxGain * 1e4).toFixed(0) + 'p', C.gn],
                ['Max Loss', (sel.maxLoss * 1e4).toFixed(0) + 'p', C.rd],
                ['P(profit)', ((sel.pp || 0) * 100).toFixed(1) + '%', C.bl],
                ['Bang/Buck', sel.bfb.toFixed(2) + 'x', C.am],
                ['Net Delta', (sel.netDelta >= 0 ? '+' : '') + sel.netDelta.toFixed(3), C.cy],
              ].map(([k, v, col]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: C.txD, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em' }}>{k}</span>
                  <span className="mono" style={{ color: col, fontWeight: 600 }}>{v}</span>
                </div>
              ))}
            </div>

            {sel.breakevens.length > 0 && (
              <div style={{ padding: '8px 16px', borderBottom: `1px solid ${C.bd}`, fontSize: 12 }}>
                <span style={{ color: C.txD, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', marginRight: 8 }}>Breakeven</span>
                {sel.breakevens.map((b, i) => (
                  <span key={i} className="mono" style={{ color: C.ac, fontWeight: 600, marginRight: 10 }}>{b.toFixed(4)}</span>
                ))}
              </div>
            )}

            <div style={{ padding: '10px 12px', borderBottom: `1px solid ${C.bd}` }}>
              <div style={{ fontSize: 10, color: C.txD, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 4 }}>
                Payoff at Expiry
              </div>
              <PayoffChart data={sel.payoffData} target={mode === 'target' ? target : null} spot={spot}
                density={dCache[sel.legs[0] && sel.legs[0].tenor]}
                rangeLo={mode === 'range' && rangeLo != null && rangeHi != null ? Math.min(rangeLo, rangeHi) : null}
                rangeHi={mode === 'range' && rangeLo != null && rangeHi != null ? Math.max(rangeLo, rangeHi) : null} />
            </div>
          </> : (
            <div style={{ padding: 40, textAlign: 'center', color: C.txM, fontSize: 13 }}>Select a strategy from the list.</div>
          )}

          <div style={{ padding: '6px 10px', borderTop: `1px solid ${C.bd}` }}>
            <div style={{ fontSize: 9, color: C.txD, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4 }}>Events</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {EVENTS.slice(0, 14).map((ev, i) => {
                const act = evFilt && evFilt.date === ev.date;
                return (
                  <div key={i} onClick={() => togEv(ev)}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 5px', borderRadius: 2, fontSize: 11, cursor: 'pointer', background: act ? C.pu + '12' : i % 2 ? 'transparent' : C.bg, border: act ? '1px solid ' + C.pu + '35' : '1px solid transparent' }}>
                    <span style={{ width: 4, height: 4, borderRadius: '50%', background: ev.type === 'cb' ? C.am : C.bl, flexShrink: 0 }} />
                    <span style={{ fontFamily: MONO, fontSize: 9, color: C.txD, width: 48 }}>{ev.date.slice(5)}</span>
                    <span style={{ color: C.tx, fontWeight: act ? 600 : 400 }}>{ev.name}</span>
                    {act && <span style={{ marginLeft: 'auto', fontSize: 6, color: C.pu }}>&#9679;</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
